import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  effect,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { content } from 'src/environments/data';
import { CartService } from '../services/cart.service';
import { CentsToCurrencyPipe } from '../pipes/cents-to-currency.pipe';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-webddncart',
  templateUrl: './webddncart.component.html',
  styleUrls: ['./webddncart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, CentsToCurrencyPipe],
})
export class WebddnCartComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cartService = inject(CartService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);

  webddn_team = (content as any).WEBDDN_TEAM ?? '';

  // ── Cart state ────────────────────────────────────────────────────────────
  readonly isOpen = this.cartService.isCartDrawerOpen;
  readonly cartItems = this.cartService.items;
  readonly cartItemCount = this.cartService.itemCount;
  readonly cartSubtotal = this.cartService.subtotal;

  readonly isUserSignedIn = computed(
    () => !!this.cookieService.get('ddnsession'),
  );

  readonly hasRegistrationItem = computed(() =>
    this.cartItems().some((item) => {
      const type = Number(
        item.product?.products_type ?? (item as any)?.products_type,
      );
      if (type === 4) return true;
      const prodType = String(
        item.product?.product_type ?? (item as any)?.product_type ?? '',
      ).toLowerCase();
      if (prodType === 'team') return true;
      if (
        (item.product as any)?.item_type === 'registration' ||
        (item as any)?.item_type === 'registration'
      )
        return true;
      if ((item.product as any)?.team_id || (item as any)?.team_id)
        return true;
      if (
        (item.product as any)?.passenger_id ||
        (item as any)?.passenger_id
      )
        return true;
      if (
        (item.product as any)?.tournamentId ||
        (item as any)?.tournamentId
      )
        return true;
      return false;
    }),
  );

  readonly checkoutButtonLabel = computed(() => {
    if (this.hasRegistrationItem() && !this.isUserSignedIn()) {
      return 'Register to Checkout';
    }
    if (!this.isUserSignedIn()) {
      return 'Checkout as Guest';
    }
    return 'Checkout';
  });

  constructor() {
    // Push app-root content left when cart flyout opens / closes
    effect(() => {
      const isOpen = this.isOpen();
      if (isPlatformBrowser(this.platformId)) {
        const appRoot = document.querySelector('app-root');
        if (appRoot) {
          appRoot.classList.toggle('nav-flyout-push-active', isOpen);
        }
      }
    });
  }

  // ── Public methods ────────────────────────────────────────────────────────
  close(): void {
    this.cartService.closeCartDrawer();
  }

  updateCartQuantity(productId: number | string, quantity: number): void {
    this.cartService.updateQuantity(productId, quantity);
  }

  removeItem(productId: number | string): void {
    this.cartService.removeFromCart(productId);
  }

  isProductType4(productOrItem: any): boolean {
    const prod = productOrItem?.product ?? productOrItem;
    const type = Number(
      prod?.products_type ??
      prod?.Product_type ??
      prod?.product_type ??
      productOrItem?.products_type ??
      productOrItem?.Product_type ??
      productOrItem?.product_type
    );
    if (type === 4) return true;
    const prodType = String(
      prod?.product_type ??
      prod?.Product_type ??
      productOrItem?.product_type ??
      productOrItem?.Product_type ??
      ''
    ).toLowerCase();
    return prodType === 'team';
  }

  proceedToCheckout(): void {
    this.close();
    if (this.hasRegistrationItem() && !this.isUserSignedIn()) {
      this.router.navigate(['/register/parent']);
    } else {
      this.router.navigate(['/checkout']);
    }
  }

  // ── Image helpers ─────────────────────────────────────────────────────────
  resolvedCartImage(image: string | undefined): string {
    if (!image) return this.getHeroDataUri();
    const trimmed = image.trim();
    if (trimmed.startsWith('<?xml') || trimmed.includes('<svg')) {
      return this.getHeroDataUri();
    }
    return image;
  }

  private getHeroDataUri(): string {
    const svg = content.HERO_IMAGE.trim();
    if (svg.startsWith('data:image/svg+xml')) {
      return svg;
    }
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }
}
